import { useState, useEffect, useContext } from 'react';
import { Briefcase } from 'lucide-react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import RichTextEditor from '../common/RichTextEditor';

// Composant pour un élément d'étape (read-only avec toggle actif/inactif uniquement)
function StepItem({ step, onUpdateStep }) {
  const canBeToggled = step.canBeDisabled !== false;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center space-x-4 flex-1">
        {/* Numéro d'ordre */}
        <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-indigo-600">{step.order}</span>
        </div>

        {/* Nom de l'étape (read-only) */}
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-900">{step.name}</span>
          {!canBeToggled && (
            <span className="ml-2 text-xs text-gray-500 italic">(obligatoire)</span>
          )}
        </div>

        {/* Toggle actif/inactif */}
        <div className="flex items-center">
          {canBeToggled ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={step.active}
                onChange={(e) => onUpdateStep(step._id, { active: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">
                {step.active ? 'Actif' : 'Inactif'}
              </span>
            </label>
          ) : (
            <span className="text-sm font-medium text-green-600">Toujours actif</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EditPosition({ isOpen, onClose, position, onPositionUpdated }) {
  const API = useContext(ApiContext);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    salary: '',
    contractType: 'CDI',
    open: true,
  });
  const [steps, setSteps] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bloquer le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Initialiser les données quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && position) {
      setFormData({
        title: position.title || '',
        description: position.description || '',
        location: position.location || '',
        salary: position.salary || '',
        contractType: position.contractType || 'CDI',
        open: position.open !== undefined ? position.open : true,
      });
      setSteps(position.steps ? [...position.steps].sort((a, b) => a.order - b.order) : []);
    }
  }, [isOpen, position]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateStep = (stepId, updates) => {
    setSteps(prev => prev.map(step =>
      step._id === stepId ? { ...step, ...updates } : step
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Mettre à jour les informations de base de la position
      await API.put(`/api/positions/${position._id}`, formData);

      // Mettre à jour uniquement le statut actif/inactif des étapes
      for (const step of steps) {
        await API.put(`/api/positions/${position._id}/steps/${step._id}`, {
          active: step.active,
        });
      }

      toast.success('Poste mis à jour avec succès');
      onPositionUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Erreur lors de la mise à jour de la position');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      {/* Overlay sombre avec effet de flou */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal avec contrainte de hauteur */}
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 relative z-10 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Briefcase className="text-white" size={18} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white m-0">Modifier le poste</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
              <form onSubmit={handleSubmit} className="space-y-6" id="edit-position-form">
                {/* Informations de base */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titre du poste *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Localisation
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Salaire
                    </label>
                    <input
                      type="text"
                      value={formData.salary}
                      onChange={(e) => handleInputChange('salary', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de contrat
                    </label>
                    <select
                      value={formData.contractType}
                      onChange={(e) => handleInputChange('contractType', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="Freelance">Freelance</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description du poste</label>
                      <div className='rounded-lg border'>
                        <RichTextEditor
                          value={formData.description || ''}
                          onChange={(value) => setFormData({ ...formData, description: value })}
                          placeholder="Décrivez le poste, les responsabilités, les missions..."
                          minHeight="250px"
                        />
                      </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.open}
                      onChange={(e) => handleInputChange('open', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">Poste ouvert</span>
                  </label>
                </div>

                {/* Gestion des étapes */}
                <div>
                  <div className="mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Étapes du processus</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Les étapes sont fixes. Vous pouvez activer ou désactiver les étapes optionnelles.
                    </p>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {steps.map((step) => (
                      <StepItem
                        key={step._id}
                        step={step}
                        onUpdateStep={handleUpdateStep}
                      />
                    ))}
                  </div>

                  {steps.length === 0 && (
                    <p className="text-gray-500 text-center py-8">
                      Aucune étape définie.
                    </p>
                  )}
                </div>

              </form>
        </div>

        {/* Buttons - fixés en bas */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="edit-position-form"
            disabled={isSubmitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Mise à jour...' : 'Mettre à jour'}
          </button>
        </div>
      </div>
    </div>
  );
}